import {Dropdown} from 'flowbite-react';
import NotificationsExists from '../assets/notifications_exist.svg';
import NotificationsEmpty from '../assets/notifications_empty.svg';
import React, {useRef} from "react";
import {useNotificationsQuery, useNotificationsUnreadQuery} from "../hooks/useNotificationsQuery";
import {LoadingMore} from "./Loading";
import {Notification} from "../types/notifications";
import {formatDateTime} from "../helpers/dates";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {deleteAllNotification, readNotification} from "../services/notifications";
import {useNavigate} from "react-router-dom";


const UnreadIndicator = ({onClick}: { onClick: () => void }) => {
    return (
        // cursor-pointer
        <div onClick={onClick} className="relative w-4 h-12 ">
            <div className="relative w-2 h-2 top-2 left-1 bg-[#ff3c3c] rounded"/>
        </div>
    );
}

interface NotificationProps {
    notification: Notification
    readFn: (pk: string) => void
}

export const NotificationItem = ({notification, readFn}: NotificationProps) => {
    const navigate = useNavigate();
    const handleClick = () => {
        if (notification?.data?.claim_pk) {
            readFn(notification.pk)
            navigate(`/claims/${notification.data.claim_pk}/`)
        }
    }
    return (
        <div
            onClick={handleClick}
            className="
            cursor-pointer
            flex flex-col w-full
            items-start
            gap-3.5 px-5 py-0
            relative flex-[0_0_auto] bg-bgcard
            ">
            <div className="flex flex-col w-full items-start gap-5 relative flex-[0_0_auto]">
                <div className="flex flex-col w-full items-start gap-4 relative flex-[0_0_auto]">
                    {/*DATETIME AND MENU*/}
                    <div className="flex w-full items-start gap-[5px] relative flex-[0_0_auto]">
                        <div className="inline-flex flex-row w-full justify-between items-center relative">
                            {/*DATETIME*/}
                            <div className="inline-flex items-start gap-[5px] relative flex-[0_0_auto]">
                                <div
                                    className="relative w-fit [font-family:'Acrom-Medium',Helvetica] font-medium text-[#949494] text-base text-right tracking-[0] leading-5 whitespace-nowrap">
                                    {notification.created_at ? formatDateTime(notification.created_at) : '--:--'}
                                </div>
                            </div>
                            {/*MENU DOTS*/}
                            {/*<div className="relative w-6 h-6 overflow-hidden">*/}
                            {/*    <div className="absolute w-[3px] h-[15px] top-[5px] left-[11px]">*/}
                            {/*        <div className="relative h-[15px]">*/}
                            {/*            <div*/}
                            {/*                className="top-0 absolute w-[3px] h-[3px] left-0 bg-[#949494] rounded-[1.5px]"/>*/}
                            {/*            <div*/}
                            {/*                className="top-1.5 absolute w-[3px] h-[3px] left-0 bg-[#949494] rounded-[1.5px]"/>*/}
                            {/*            <div*/}
                            {/*                className="top-3 absolute w-[3px] h-[3px] left-0 bg-[#949494] rounded-[1.5px]"/>*/}
                            {/*        </div>*/}
                            {/*    </div>*/}
                            {/*</div>*/}
                        </div>
                    </div>
                    {/*DETAILS*/}
                    <div className="flex flex-col w-full items-start gap-2 relative flex-[0_0_auto]">
                        {/*TITLE*/}
                        <div className="inline-flex items-start relative flex-[0_0_auto]">
                            {/*UNREAD INDICATOR*/}
                            {!notification.read_at && <UnreadIndicator onClick={() => {
                            }}/>} {/*readFn(notification.pk)*/}
                            {/*TITLE TEXT*/}
                            <p className="relative w-full mt-[-1.00px] font-manager-acrom-h3-20-medium-acrom font-[number:var(--manager-acrom-h3-20-medium-acrom-font-weight)] text-black text-[length:var(--manager-acrom-h3-20-medium-acrom-font-size)] tracking-[var(--manager-acrom-h3-20-medium-acrom-letter-spacing)] leading-[var(--manager-acrom-h3-20-medium-acrom-line-height)] [font-style:var(--manager-acrom-h3-20-medium-acrom-font-style)]">
                                {notification.title}
                            </p>
                        </div>
                        {/*MESSAGE*/}
                        <p className="relative w-full font-manager-acrom-ph1-16-medium-acrom font-[number:var(--manager-acrom-ph1-16-medium-acrom-font-weight)] text-[#949494] text-[length:var(--manager-acrom-ph1-16-medium-acrom-font-size)] tracking-[var(--manager-acrom-ph1-16-medium-acrom-letter-spacing)] leading-[var(--manager-acrom-ph1-16-medium-acrom-line-height)] [font-style:var(--manager-acrom-ph1-16-medium-acrom-font-style)]">
                            {notification.body}
                        </p>
                    </div>
                </div>
            </div>
            <div className="relative w-full h-px bg-line"/>
        </div>
    );
}

export const NotificationsDropdown = () => {
    const queryClient = useQueryClient();

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useNotificationsQuery()
    const {
        data: unread,
        isSuccess: unreadLoaded,
    } = useNotificationsUnreadQuery()

    const {mutate: read} = useMutation({
        mutationFn: readNotification,
        onSuccess: (data) => {
            invalidateNotifications();
            invalidateUnread();
        }
    });
    const {mutate: clearAll} = useMutation({
        mutationFn: deleteAllNotification,
        onSuccess: (data) => {
            invalidateNotifications();
            invalidateUnread();
        }
    });

    // Для индкатора непрочитанных уведомлений
    const invalidateUnread = () => {
        queryClient.invalidateQueries({
            queryKey: ['unreadNotifications'],
        });
    }

    // Для списка уведомлений
    const invalidateNotifications = () => {
        queryClient.invalidateQueries({
            queryKey: ['notifications'],
        });
    }


    const handleRead = (pk: string) => {
        read(pk);
    }

    const handleClearAll = () => {
        clearAll();
    }

    // Ref на выпадающий список
    const dropdownRef = useRef(null);

    const handleScroll = () => {
        if (dropdownRef.current) {
            const {scrollTop, scrollHeight, clientHeight} = dropdownRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 5) {
                if (hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            }
        }
    };

    return (
        <Dropdown
            className="
            flex flex-col z-20 relative
            w-96
            min-h-56
            max-h-[85vh]
            h-max
            bg-white rounded-lg overflow-hidden"
            arrowIcon={false}
            inline
            label={
                <img className='bg-white p-3 rounded-lg' src={
                    unreadLoaded && unread?.amount ? NotificationsExists : NotificationsEmpty
                } alt='Notifications'/>
            }
        >
            <Dropdown.Header
                className="flex flex-row h-16 w-full justify-between items-center bg-white rounded-t-lg overflow-hidden shadow-shadow-card">
                <div
                    className="inline-flex justify-center items-center font-manager-acrom-h3-20-medium-acrom font-[number:var(--manager-acrom-h3-20-medium-acrom-font-weight)] text-black text-[length:var(--manager-acrom-h3-20-medium-acrom-font-size)] tracking-[var(--manager-acrom-h3-20-medium-acrom-letter-spacing)] leading-[var(--manager-acrom-h3-20-medium-acrom-line-height)] whitespace-nowrap [font-style:var(--manager-acrom-h3-20-medium-acrom-font-style)]">
                    Уведомления
                </div>
                <button className="inline-flex items-center justify-center gap-2.5 px-5 py-2.5 bg-[#f2f2f2] rounded-lg">
                    <div
                        onClick={handleClearAll}
                        className="relative w-fit mt-[-1.00px] font-manager-acrom-ph1-16-medium-acrom font-[number:var(--manager-acrom-ph1-16-medium-acrom-font-weight)] text-black text-[length:var(--manager-acrom-ph1-16-medium-acrom-font-size)] tracking-[var(--manager-acrom-ph1-16-medium-acrom-letter-spacing)] leading-[var(--manager-acrom-ph1-16-medium-acrom-line-height)] whitespace-nowrap [font-style:var(--manager-acrom-ph1-16-medium-acrom-font-style)]">
                        Очистить всё
                    </div>
                </button>
            </Dropdown.Header>
            {/*LIST*/}
            <div
                ref={dropdownRef}
                onScroll={handleScroll}
                className="
                    flex flex-col
                    mt-6
                    min-h-56
                    max-h-[70vh]
                    overflow-y-scroll
                    items-start gap-5"
            >
                {data?.pages.map((page, i) => (
                    <React.Fragment key={i}>
                        {page.map((e: Notification, index) => (
                            <NotificationItem key={index} readFn={handleRead} notification={e}/>
                        ))}
                    </React.Fragment>
                ))}
            </div>
            {/*Индикатор подгрузки*/}
            {isFetchingNextPage && <LoadingMore/>}
        </Dropdown>
    );
}
